/**
 * Prediction Model Version Configurations
 *
 * This file defines all model versions with their specific weights and parameters.
 * Each version represents a distinct configuration that can be compared for accuracy.
 *
 * Version Naming Convention:
 * - Major (1.0 → 2.0): Fundamental algorithm changes, new data sources
 * - Minor (1.0 → 1.1): Weight adjustments, context factor tuning, formula tweaks
 * - Patch (1.0.0 → 1.0.1): Bug fixes only (no logic changes)
 */

import { CURRENT_MODEL_VERSION } from '@shared/constants/model-version';
import { PassRushPredictionConfig } from '@shared/types/pass-rush-prediction.types';

export interface IOffensiveWeights {
  PTS_FOR: number; // Points scored
  YDS: number; // Total yards
  RZ_PCT: number; // Red zone efficiency
  '3RD_PCT': number; // Third down conversion %
  FUM_LOST: number; // Fumbles lost (negative weight)
}

export interface IDefensiveWeights {
  PTS_ALLOW: number; // Points allowed
  YDS_ALLOW: number; // Total yards allowed
  SACKS: number; // Sacks by defense
  INT: number; // Interceptions by defense
  RZ_PCT_STOP: number; // Red zone stops %
  '3RD_PCT_STOP': number; // Third down stops %
}

export interface IContextAdjustments {
  // Home field advantage
  homeFieldIndoor: number; // Points added for indoor home games
  homeFieldOutdoor: number; // Points added for outdoor home games

  // Rest differential
  restDaysAdvantage: number; // Points per day of extra rest (when diff >= 3 days)

  // Weather (outdoor only)
  adverseWeatherMultiplier: number; // Multiplier for offense in bad weather (e.g., 0.9 = -10%)
  severeWeatherMultiplier: number; // Multiplier for extreme conditions (e.g., 0.85 = -15%)

  // Division rivalry
  divisionRivalryMultiplier: number; // Reduces predicted margin (e.g., 0.85 = closer games)

  // Prime time
  primeTimeHomeBoost: number; // Points added to home team in prime time

  // Playoff implications
  playoffImplicationsBoost: number; // Points added when stakes are high
}

export interface IConsistencyWeights {
  clutchImpact: number; // Points per clutch rating diff
  reliabilityThreshold: number; // Below this = volatile
  consistencyImpact: number; // Weight for general consistency
}

export interface IPredictionModelConfig {
  passRushModel?: PassRushPredictionConfig;
  version: string;
  description: string;
  offensiveWeights: IOffensiveWeights;
  defensiveWeights: IDefensiveWeights;
  contextAdjustments: IContextAdjustments;
  scorePredictionDivisor: number; // Divisor for adjustedScore when calculating predicted scores
  confidenceThresholds: {
    high: number; // adjustedScore >= this value
    medium: number; // adjustedScore >= this value (< high)
    // low is everything below medium
  };
  maxConfidenceEarlySeason: number; // Cap for Weeks 1-3 (e.g., 95%)
  earlySeasonWeeks: number; // Number of weeks considered "early season" (e.g., 3)
  consistencyWeights?: IConsistencyWeights; // Optional for backward compatibility
}

/**
 * Model Version Registry
 *
 * All model versions are stored here with their complete configurations.
 * This allows for easy A/B testing and rollback to previous versions.
 */
export const PREDICTION_MODEL_VERSIONS: Record<string, IPredictionModelConfig> = {
  /**
   * v1.0.0 - Initial Release (DEPRECATED - Win Probability Bug)
   *
   * First working version with core algorithm.
   * KNOWN ISSUE: Win probability shows home team % instead of winner's %
   * DO NOT USE - Fixed in v1.0.1
   */
  '1.0.0': {
    version: '1.0.0',
    description: 'Initial release - DEPRECATED due to win probability bug',
    offensiveWeights: {
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 3,
      homeFieldOutdoor: 5,
      restDaysAdvantage: 2,
      adverseWeatherMultiplier: 0.9,
      severeWeatherMultiplier: 0.85,
      divisionRivalryMultiplier: 0.85,
      primeTimeHomeBoost: 1,
      playoffImplicationsBoost: 2,
    },
    scorePredictionDivisor: 4,
    confidenceThresholds: {
      high: 20,
      medium: 10,
    },
    maxConfidenceEarlySeason: 100, // No cap
    earlySeasonWeeks: 0, // No early season handling
  },

  /**
   * v1.0.1 - Bug Fix Release (CURRENT PRODUCTION)
   *
   * Fixed win probability bug where predicted winner's probability was incorrect.
   * Changed line 334 in generatePrediction() to use adjustedScore for winner determination.
   *
   * Test Results (2025 Week 1):
   * - 16/16 games processed successfully
   * - Performance: ~200ms batch generation
   * - Issues identified: Extreme predictions due to Week 1 data volatility
   *
   * Known Issues:
   * - Extreme score predictions (46-0, 51-0) with Week 1 data
   * - Confidence levels hitting 100% and 0-2%
   * - Score divisor (4) too aggressive for early season
   */
  '1.0.1': {
    version: '1.0.1',
    description: "Bug fix: Win probability now shows predicted winner's percentage",
    offensiveWeights: {
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 3,
      homeFieldOutdoor: 5,
      restDaysAdvantage: 2,
      adverseWeatherMultiplier: 0.9,
      severeWeatherMultiplier: 0.85,
      divisionRivalryMultiplier: 0.85,
      primeTimeHomeBoost: 1,
      playoffImplicationsBoost: 2,
    },
    scorePredictionDivisor: 4,
    confidenceThresholds: {
      high: 20,
      medium: 10,
    },
    maxConfidenceEarlySeason: 100, // No cap yet
    earlySeasonWeeks: 0, // No early season handling yet
  },

  /**
   * v1.1.0 - Major Score Formula Overhaul & Context Weight Reduction
   *
   * **Created:** December 8, 2025
   * **Based on:** 2023 Weeks 1-4 analysis (64 games, v1.0.1 baseline)
   *
   * **Critical Fixes from v1.0.1 Analysis:**
   * 1. Score formula completely rewritten (was producing 0-68 point ranges)
   * 2. Context weights significantly reduced (home field 3.0→1.5, rest 2.0→0.3)
   * 3. Confidence thresholds recalibrated (20/10→7/3.5 based on realistic margins)
   * 4. Win probability caps added (5-95% range, no more 100%/0% predictions)
   * 5. Division boost reduced but maintained (working at 65% accuracy)
   * 6. Prime time boost removed (no evidence of effectiveness)
   *
   * **v1.0.1 Problems Fixed:**
   * - ❌ 50% overall accuracy (coin-flip) → Target: 55-58%
   * - ❌ 15 shutout predictions in 64 games → Target: 0-1
   * - ❌ Average margin error: 16.4 points → Target: 8-10 points
   * - ❌ Medium confidence: 27% accurate → Target: 48-52%
   * - ❌ Conference games: 37% accurate → Target: 45-50%
   *
   * **Testing Plan:**
   * - Re-run 2023 Weeks 1-4 with v1.1.0
   * - Compare side-by-side with v1.0.1 baseline
   * - Validate improvements in all problem areas
   * - If successful (>55% accuracy), continue to Weeks 5-8
   *
   * **Key Architecture Changes:**
   * - Score formula now includes regression to mean, min/max bounds
   * - Strength multiplier reduced from ~0.5 to 0.20 (60% reduction)
   * - Confidence based on predicted margin (not adjustedScore)
   */
  '1.1.0': {
    version: '1.1.0',
    description: 'Post Week 1-4 analysis: Conservative scoring, reduced context weights, recalibrated confidence',
    offensiveWeights: {
      // Keep same as v1.0.1 - not the problem
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      // Keep same as v1.0.1 - not the problem
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 1.5, // Reduced from 3 (50% reduction)
      homeFieldOutdoor: 2.5, // Reduced from 5 (50% reduction)
      restDaysAdvantage: 0.3, // Reduced from 2 (85% reduction)
      adverseWeatherMultiplier: 0.9, // Keep same (limited data on effectiveness)
      severeWeatherMultiplier: 0.85, // Keep same (limited data on effectiveness)
      divisionRivalryMultiplier: 0.9, // Reduced from 0.85 (working well at 65%, slight reduction)
      primeTimeHomeBoost: 0, // REMOVED (was 1, no evidence of helping)
      playoffImplicationsBoost: 1, // Reduced from 2 (50% reduction)
    },
    scorePredictionDivisor: 0, // NOT USED in v1.1.0 - new formula doesn't use divisor
    confidenceThresholds: {
      high: 7.0, // Based on predicted MARGIN (was 20 on adjustedScore)
      medium: 3.5, // Based on predicted MARGIN (was 10 on adjustedScore)
      // Rationale: NFL average margin is ~10 pts, 7+ is substantial, 3.5-7 is moderate
    },
    maxConfidenceEarlySeason: 95, // Cap at 95% for Weeks 1-3 (no more 100% predictions)
    earlySeasonWeeks: 3, // Weeks 1-3 considered "early season" (limited data)
  },

  /**
   * v1.2.0 - Consistency Integration
   *
   * **Target Date:** December 2025
   * **Base Version:** v1.1.0 (Stable Math)
   * **Primary Goal:** Increase predictive accuracy from ~47% to >55% by integrating Team Consistency Metrics.
   *
   * **Key Features:**
   * 1. Penalize Inconsistent Teams: Reduce confidence in teams that play erratically.
   * 2. Reward Clutch Teams: Give the edge to teams with high "Clutch Factors" in close games.
   * 3. Contextualize Matchups: Use "Game Script Reliability" to refine predictions.
   */
  '1.2.0': {
    version: '1.2.0',
    description: 'Integrates Consistency Metrics (Reliability, Clutch Factor) to improve close game accuracy',
    offensiveWeights: {
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 1.5,
      homeFieldOutdoor: 2.5,
      restDaysAdvantage: 0.3,
      adverseWeatherMultiplier: 0.9,
      severeWeatherMultiplier: 0.85,
      divisionRivalryMultiplier: 0.9,
      primeTimeHomeBoost: 0,
      playoffImplicationsBoost: 1,
    },
    scorePredictionDivisor: 0,
    confidenceThresholds: {
      high: 7.0,
      medium: 3.5,
    },
    maxConfidenceEarlySeason: 95,
    earlySeasonWeeks: 3,
    consistencyWeights: {
      clutchImpact: 0.25, // Increased from 0.1 to make it matter
      reliabilityThreshold: 70, // Increased from 60 to catch more volatile teams
      consistencyImpact: 0.1, // Increased from 0.05
    },
  },

  /**
   * v1.2.1 - Consistency Ramp-Up
   *
   * **Target Date:** December 2025
   * **Base Version:** v1.2.0
   * **Primary Goal:** Fix early-season regression by ramping up consistency weights over time.
   *
   * **Key Changes:**
   * 1. Weeks 1-4: 0% Consistency Impact (Too noisy).
   * 2. Weeks 5-8: Scaled Impact (25% -> 100%).
   * 3. Weeks 9+: Full Impact.
   */
  '1.2.1': {
    version: '1.2.1',
    description: 'Ramps up Consistency Metrics impact from Week 5-8 to avoid early season noise',
    offensiveWeights: {
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 1.5,
      homeFieldOutdoor: 2.5,
      restDaysAdvantage: 0.3,
      adverseWeatherMultiplier: 0.9,
      severeWeatherMultiplier: 0.85,
      divisionRivalryMultiplier: 0.9,
      primeTimeHomeBoost: 0,
      playoffImplicationsBoost: 1,
    },
    scorePredictionDivisor: 0,
    confidenceThresholds: {
      high: 7.0,
      medium: 3.5,
    },
    maxConfidenceEarlySeason: 95,
    earlySeasonWeeks: 3,
    consistencyWeights: {
      clutchImpact: 0.25,
      reliabilityThreshold: 70,
      consistencyImpact: 0.1,
    },
  },

  /**
   * v1.2.2 - Consistency Ramp-Up + Indexing Fix
   *
   * **Target Date:** December 2025
   * **Base Version:** v1.2.1
   * **Primary Goal:** Fix data leakage bug in v1.2.1 where Week 5 predictions used empty Week 5 stats instead of Week 4 stats.
   *
   * **Key Changes:**
   * 1. Same Ramp-Up logic as v1.2.1.
   * 2. Fixed indexing offset for previous week stats (numericWeekID - 2).
   *
   * **Known Issue:** Double-subtraction bug causes predictions to use data from 2 weeks prior instead of previous week.
   */
  '1.2.2': {
    version: '1.2.2',
    description: 'Fixes indexing bug in v1.2.1 (Week 5 data leakage). Retains Consistency Ramp-Up.',
    offensiveWeights: {
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 1.5,
      homeFieldOutdoor: 2.5,
      restDaysAdvantage: 0.3,
      adverseWeatherMultiplier: 0.9,
      severeWeatherMultiplier: 0.85,
      divisionRivalryMultiplier: 0.9,
      primeTimeHomeBoost: 0,
      playoffImplicationsBoost: 1,
    },
    scorePredictionDivisor: 0,
    confidenceThresholds: {
      high: 7.0,
      medium: 3.5,
    },
    maxConfidenceEarlySeason: 95,
    earlySeasonWeeks: 3,
    consistencyWeights: {
      clutchImpact: 0.25,
      reliabilityThreshold: 70,
      consistencyImpact: 0.1,
    },
  },

  /**
   * v1.3.0 - Double-Subtraction Bug Fix
   *
   * **Target Date:** December 31, 2025
   * **Base Version:** v1.2.2
   * **Primary Goal:** Fix double-subtraction bug causing predictions to use data from 2 weeks prior instead of previous week.
   *
   * **Bug Description:**
   * In v1.2.2 and earlier, `getAggregatedGameDataForPredictions` calculates `previousSeasonWeekID` by subtracting 1
   * from the current week (e.g., Week 18 → Week 17). However, `extractCategoryValueByWeekID` was also subtracting 2
   * (numericWeekID - 2), resulting in accessing data from 2 weeks prior (e.g., Week 16 data when predicting Week 18).
   *
   * **Key Changes:**
   * 1. Changed `extractCategoryValueByWeekID` from `numericWeekID - 2` to `numericWeekID - 1`.
   * 2. Corrected JSDoc to reflect that caller pre-calculates the target week.
   * 3. All weights and thresholds remain identical to v1.2.2.
   *
   * **Expected Impact:**
   * - Predictions now use correct previous week data (Week 17 for Week 18 predictions).
   * - Should eliminate rank data mismatches and inverted predictions.
   * - Expected accuracy improvement from 58.67% to 62-68%.
   *
   * **Known Issue:** TeamConsistency data has bye week bug causing CV/variance spikes. Fixed in v1.3.1.
   */
  '1.3.0': {
    version: '1.3.0',
    description: 'Fixes double-subtraction bug - predictions now use correct previous week data.',
    offensiveWeights: {
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 1.5,
      homeFieldOutdoor: 2.5,
      restDaysAdvantage: 0.3,
      adverseWeatherMultiplier: 0.9,
      severeWeatherMultiplier: 0.85,
      divisionRivalryMultiplier: 0.9,
      primeTimeHomeBoost: 0,
      playoffImplicationsBoost: 1,
    },
    scorePredictionDivisor: 0,
    confidenceThresholds: {
      high: 7.0,
      medium: 3.5,
    },
    maxConfidenceEarlySeason: 95,
    earlySeasonWeeks: 3,
    consistencyWeights: {
      clutchImpact: 0.25,
      reliabilityThreshold: 70,
      consistencyImpact: 0.1,
    },
  },

  /**
   * v1.3.1 - Bye Week Data Fix
   *
   * **Target Date:** January 1, 2026
   * **Base Version:** v1.3.0
   * **Primary Goal:** Fix bye week data initialization to properly mark bye weeks as 'BYE' instead of 0.
   *
   * **Bug Description:**
   * Weekly team stats (`{year}-Teams` collection) stored numeric `0` at bye weeks instead of string `'BYE'`.
   * This caused consistency calculations (CV, variance, stdDev) to treat bye weeks as real games with 0 stats,
   * resulting in 400-600% coefficient of variation spikes that never recovered. Teams appeared inconsistent
   * post-bye, triggering reliability penalties for the rest of the season.
   *
   * **Root Cause:**
   * `initializeAccumulatedStats()` in generateTeamStatsHandler.ts set metadata (uID, gameID) for bye weeks
   * but didn't set stat category arrays to 'BYE'. Consistency calculations correctly check for 'BYE' string
   * but received numeric 0, treating it as real data.
   *
   * **Key Changes:**
   * 1. Backend: Added loop in `initializeAccumulatedStats` to set all stat categories to 'BYE' at bye week positions.
   * 2. Frontend: Applied identical fix to AdminStatsAPI.ts `initializeAccumulatedStats`.
   * 3. All model weights and thresholds remain identical to v1.3.0.
   *
   * **Data Regeneration Required:**
   * - Regenerate weekly stats for 2023-2025 seasons (fixes bye week markers)
   * - Regenerate consistency metrics for all seasons/weeks (eliminates CV spikes)
   *
   * **Expected Impact:**
   * - Coefficient of variation remains stable through bye weeks (no more 42.31 spikes from 7.44).
   * - Standard deviation and variance calculations exclude bye weeks properly.
   * - Reliability scores no longer penalized for false inconsistency post-bye.
   * - Expected accuracy improvement: 3-5% in bye week games and post-bye predictions.
   */
  '1.3.1': {
    version: '1.3.1',
    description: 'Fixes bye week data initialization - eliminates CV spikes and false inconsistency penalties.',
    offensiveWeights: {
      PTS_FOR: 0.3,
      YDS: 0.2,
      RZ_PCT: 0.25,
      '3RD_PCT': 0.15,
      FUM_LOST: 0.1,
    },
    defensiveWeights: {
      PTS_ALLOW: 0.3,
      YDS_ALLOW: 0.2,
      SACKS: 0.15,
      INT: 0.1,
      RZ_PCT_STOP: 0.15,
      '3RD_PCT_STOP': 0.1,
    },
    contextAdjustments: {
      homeFieldIndoor: 1.5,
      homeFieldOutdoor: 2.5,
      restDaysAdvantage: 0.3,
      adverseWeatherMultiplier: 0.9,
      severeWeatherMultiplier: 0.85,
      divisionRivalryMultiplier: 0.9,
      primeTimeHomeBoost: 0,
      playoffImplicationsBoost: 1,
    },
    scorePredictionDivisor: 0,
    confidenceThresholds: {
      high: 7.0,
      medium: 3.5,
    },
    maxConfidenceEarlySeason: 95,
    earlySeasonWeeks: 3,
    consistencyWeights: {
      clutchImpact: 0.25,
      reliabilityThreshold: 70,
      consistencyImpact: 0.1,
    },
  },

  /**
   * v1.4.0 - Defense Wins Championships
   *
   * **Created:** January 11, 2026
   * **Based on:** 2025 Playoff Team Analysis (14 playoff teams, 32 total teams)
   *
   * **Key Insight:** Defense is MORE predictive than offense for playoff success.
   * - Defensive stats cluster tightly (ranks 1-13 = top 40% of league)
   * - Offensive stats spread wider (ranks 1-18 = top 56% of league)
   * - Elite defense is NECESSARY; offense can be "good enough"
   *
   * **Most Predictive Stats:**
   * Defense:
   * - Points Allowed: 10/14 teams in ranks 1-13 (71% in top 40%)
   * - Total TDs Allowed: Same tight clustering (1-13)
   * - Defensive TDs: Same pattern (1-13, 19, 26)
   * - RZ Efficiency: Ranks 1-19 (controlled scoring in critical situations)
   * - Interceptions: Ranks 1-15 (takeaway creation)
   *
   * Offense:
   * - Points Scored: Ranks 1-18 (wider spread, less critical)
   * - RZ Efficiency: Ranks 1-18, 23, 26, 27 (still important)
   * - Sacks Taken: Ranks 1-12 (fewer is better, OL quality)
   * - Turnovers: Ranks 1-16 (protecting the ball)
   *
   * **Less Predictive (Omitted/Reduced):**
   * - Rushing Attempts: 13/14 teams in ranks 1-19, 28 (94% of league!)
   * - First Downs: Bimodal distribution (game script dependent)
   * - Passing Attempts Against: All over the place (game script)
   *
   * **Weight Philosophy Change:**
   * - Previous models: ~equal offense/defense weight (~40% each)
   * - v1.4.0: Defense 48% | Offense 32% | Context 20%
   * - Emphasize Points + RZ Efficiency on both sides
   * - Defensive takeaways > offensive turnovers in importance
   * - Volume stats (yards, attempts) de-emphasized
   *
   * **Expected Improvement:**
   * - Current v1.3.1: 57% accuracy (308/544 games over 2 seasons)
   * - Target v1.4.0: 60-62% accuracy by better weighting defensive performance
   * - Hypothesis: Tight defensive clustering means this is the differentiator
   */
  '1.4.0': {
    version: '1.4.0',
    description:
      'Defense-focused model based on 2025 playoff analysis - defensive stats are more predictive (tight clustering)',
    offensiveWeights: {
      PTS_FOR: 0.35, // Increased from 0.3 (still critical, but offense less predictive overall)
      YDS: 0.1, // Decreased from 0.2 (wider spread, game script dependent)
      RZ_PCT: 0.3, // Increased from 0.25 (ranks 1-18, efficiency matters)
      '3RD_PCT': 0.15, // Keep same (sustained drives indicator)
      FUM_LOST: 0.1, // Keep same (turnovers matter)
    },
    defensiveWeights: {
      PTS_ALLOW: 0.4, // Increased from 0.3 (TIGHTEST clustering: ranks 1-13)
      YDS_ALLOW: 0.1, // Decreased from 0.2 (less predictive, volume stat)
      SACKS: 0.15, // Keep same (pressure is important)
      INT: 0.15, // Increased from 0.1 (ranks 1-15, takeaways critical)
      RZ_PCT_STOP: 0.15, // Keep same (bend don't break, ranks 1-19)
      '3RD_PCT_STOP': 0.05, // Decreased from 0.1 (less critical than other metrics)
    },
    contextAdjustments: {
      homeFieldIndoor: 1.5, // Keep same from v1.3.1
      homeFieldOutdoor: 2.5, // Keep same from v1.3.1
      restDaysAdvantage: 0.3, // Keep same from v1.3.1
      adverseWeatherMultiplier: 0.9, // Keep same
      severeWeatherMultiplier: 0.85, // Keep same
      divisionRivalryMultiplier: 0.9, // Keep same from v1.3.1
      primeTimeHomeBoost: 0, // Keep disabled
      playoffImplicationsBoost: 1, // Keep same from v1.3.1
    },
    scorePredictionDivisor: 0, // Use v1.1.0+ formula (no divisor)
    confidenceThresholds: {
      high: 7.0, // Keep same from v1.3.1
      medium: 3.5, // Keep same from v1.3.1
    },
    maxConfidenceEarlySeason: 95, // Keep cap for Weeks 1-3
    earlySeasonWeeks: 3, // Keep same from v1.3.1
    consistencyWeights: {
      clutchImpact: 0.25, // Keep same from v1.3.1
      reliabilityThreshold: 70, // Keep same from v1.3.1
      consistencyImpact: 0.1, // Keep same from v1.3.1
    },
  },

  /**
   * v1.4.1 - Context-Focused Optimization
   *
   * **Created:** January 11, 2026
   * **Based on:** v1.4.0 testing results (2 additional correct predictions out of 816 games = 0.24% improvement)
   *
   * **Key Insight:** Weight redistribution within offense/defense categories has minimal impact
   * because analyzeMatchup() treats them equally. Context adjustments are the remaining optimization
   * lever within model config constraints.
   *
   * **Problem with v1.4.0:**
   * - Only improved from 464/816 (56.86%) to 466/816 (57.11%)
   * - Root cause: analyzeMatchup() formula is symmetric: matchupScore = (homeOffVsAwayDef - awayOffVsHomeDef)
   * - Redistributing 40% defense weights (e.g., PTS_ALLOW 0.4) vs 32% offense weights has no net effect
   * - To truly weight defense more requires formula change (outside model config scope)
   *
   * **v1.4.1 Strategy: Focus Power Where We Have Data**
   *
   * **Increased Adjustments (actual data available):**
   * - Home Field Indoor: 1.5 → 2.0 (+33%) - controlled environment, we have venue data
   * - Home Field Outdoor: 2.5 → 3.5 (+40%) - crowd noise, familiarity, weather familiarity
   * - Rest Days Advantage: 0.3 → 0.5 (+67%) - we have complete game schedules
   * - Prime Time Home Boost: 0 → 1.5 (re-enabled) - we have game time data
   * - Playoff Implications: 1.0 → 2.0 (+100%) - we have standings, desperation factor
   *
   * **Disabled (no data, avoid false signals):**
   * - Adverse Weather: 0.9 → 1.0 (neutral) - weather data not tracked
   * - Severe Weather: 0.85 → 1.0 (neutral) - weather data not tracked
   *
   * **Tightened (known divisions):**
   * - Division Rivalry: 0.9 → 0.85 (-5%) - more game intensity, tighter outcomes
   *
   * **Offensive/Defensive Weights:**
   * - Kept v1.4.0 distribution unchanged
   * - Rationale: Weight redistribution ineffective given symmetric matchup formula
   * - Focus optimization on context where we can make real differences
   *
   * **Expected Improvement:**
   * - Baseline v1.3.1: 57% accuracy (308/544 games)
   * - v1.4.0 result: 57.11% (+0.24%, essentially no change)
   * - v1.4.1 target: 58-60% by leveraging confirmed contextual factors
   * - Hypothesis: Home field, rest, and playoff stakes should show measurable impact
   */
  '1.4.1': {
    version: '1.4.1',
    description:
      'Context-focused adjustments: Weather disabled (no data), home field +33-40%,' +
      'rest +67%, division -5%, prime time re-enabled, playoff stakes +100%. ' +
      'Keeps v1.4.0 defensive weights. Focuses power where we have actual data.',
    offensiveWeights: {
      // Keep defensive-focused offensive weights from v1.4.0
      PTS_FOR: 0.35, // +0.05 from v1.3.1 (30% → 35%)
      YDS: 0.1, // -0.10 from v1.3.1 (20% → 10%)
      RZ_PCT: 0.3, // +0.05 from v1.3.1 (25% → 30%)
      '3RD_PCT': 0.15, // Same as v1.3.1
      FUM_LOST: 0.1, // Same as v1.3.1
    },
    defensiveWeights: {
      // Keep defensive-focused defensive weights from v1.4.0
      PTS_ALLOW: 0.4, // +0.10 from v1.3.1 (30% → 40%)
      YDS_ALLOW: 0.1, // -0.10 from v1.3.1 (20% → 10%)
      INT: 0.15, // +0.05 from v1.3.1 (10% → 15%)
      SACKS: 0.2, // Same as v1.3.1
      '3RD_PCT_STOP': 0.05, // -0.05 from v1.3.1 (10% → 5%)
      RZ_PCT_STOP: 0.1, // Same as v1.3.1
    },
    contextAdjustments: {
      // HOME FIELD: Increase advantage (we have venue data)
      homeFieldIndoor: 2.0, // +33% from v1.3.1 (1.5 → 2.0) - controlled environment
      homeFieldOutdoor: 3.5, // +40% from v1.3.1 (2.5 → 3.5) - crowd noise, familiarity

      // WEATHER: DISABLED - No weather data available
      adverseWeatherMultiplier: 1.0, // Neutral (was 0.9 in v1.3.1)
      severeWeatherMultiplier: 1.0, // Neutral (was 0.85 in v1.3.1)

      // REST DIFFERENTIAL: Increase impact (we have game schedules)
      restDaysAdvantage: 0.5, // +67% from v1.3.1 (0.3 → 0.5) - extra rest matters

      // DIVISION RIVALRY: Tighten slightly (we know divisions)
      divisionRivalryMultiplier: 0.85, // -5% from v1.3.1 (0.9 → 0.85) - more intensity

      // PRIME TIME: Re-enable (we have game time data)
      primeTimeHomeBoost: 1.5, // Restored from 0 in v1.3.1 - home crowd energy

      // PLAYOFF IMPLICATIONS: Boost significantly (we have standings)
      playoffImplicationsBoost: 2.0, // +100% from v1.3.1 (1.0 → 2.0) - desperation factor
    },
    scorePredictionDivisor: 0, // Use v1.1.0+ formula (no divisor)
    confidenceThresholds: {
      high: 7.0, // Keep same from v1.3.1
      medium: 3.5, // Keep same from v1.3.1
    },
    maxConfidenceEarlySeason: 95, // Keep cap for Weeks 1-3
    earlySeasonWeeks: 3, // Keep same from v1.3.1
    consistencyWeights: {
      clutchImpact: 0.25, // Keep same from v1.3.1
      reliabilityThreshold: 70, // Keep same from v1.3.1
      consistencyImpact: 0.1, // Keep same from v1.3.1
    },
  },
};

/**
 * Current active model version
 * Change this to switch which model is used for new predictions
 *
 * @note: moved to import { CURRENT_MODEL_VERSION } from '@shared/constants/model-version';
 * export const CURRENT_MODEL_VERSION = '1.3.1';
 *
 */

/**
 * v1.5.0 - Opponent-Adjusted Pass/Rush Performance
 *
 * **Created:** October 13, 2026
 * **Based on:** The v1.4.1 calculation audit and the Atlanta/Baltimore pre-Week-5 worked example.
 * **Status:** Frozen after the 2023 evaluation. Coefficient or behavior changes require a new model version.
 *
 * **Key Insight:** A team's overall rank can hide the matchup that matters. A strong rushing
 * offense should face the opponent's rushing defense, with the team's actual pass/rush usage
 * determining how much each matchup contributes. Recent improvement needs evidence without
 * allowing a few touchdowns or turnovers to dominate the estimate.
 *
 * **Changes from v1.4.x:**
 * - Uses four performance ratings: passing/rushing offense and passing/rushing defense.
 * - Builds rates from stored weekly categories and actual played-game opportunities, not ranks.
 * - Blends season-to-date and recent performance, then adjusts for historical opponents.
 * - Replaces legacy consistency/context bonuses with explicit sample restraint and point adjustments.
 * - Keeps the existing version selection, prediction storage, grading and accuracy-reporting flows.
 *
 * **Input Window and Opponent Adjustment:**
 * - Only completed games before the target week count; byes add no observations.
 * - Each window uses summed numerators / summed opportunities, with a 70% season / 30% recent blend.
 * - Correct observed rates by 50% of the difference between league-average and opponent performance.
 * - Opponent estimates exclude meetings with the assessed team and are restrained toward league average.
 * - No independent opponent observations means no correction, notably entering Week 2.
 * - Required missing/mismatched game data stops the weekly pass rather than inventing neutral games.
 *
 * **Ratings and Sample Restraint:**
 * - Passing: 60% gross yards/attempt, 20% TD rate, 10% INT rate and 10% sack rate.
 * - Gross passing yards = PASS_YDS + SACK_YDS; dropbacks = ATT + SACKED.
 * - Rushing: 80% yards/attempt and 20% TD rate. Defensive rates use the matching opponent's offense.
 * - Efficiency and sacks use a three-game-equivalent prior; TDs, INTs and lost fumbles use six.
 * - Opportunity counts determine restraint; overlapping recent/season windows do not add evidence.
 * - Normalize against fixed, equal-team league means/spreads; cap each final component at +/-3.
 * - Zero opportunities/spread yield neutral components; usage shares themselves are not restrained.
 *
 * **Matchup and Expected Points:**
 * - 95% of the assessment is the usage-weighted pass/rush offense-minus-opposing-defense advantage.
 * - 5% is ball security: normalized lost fumbles per played game, including return fumbles.
 * - Expected points = league points per team-game + 4 * matchup assessment.
 * - Add a +2 home margin and 0.15 points per extra rest day, with the rest differential capped at +/-7.
 * - Split the context margin equally between the two scores, then floor each expected score at zero.
 * - Derive margin and logistic win probability from those unrounded scores, using fixed scale 12.
 * - Favored probability sets confidence: low below 60%, medium 60%-<75%, high at least 75%.
 * - Exact 50/50 uses one repeatable game/model-seeded coin flip; scores and probability stay unchanged.
 *
 * **Week 1 and Deferred Effects:**
 * - Start with neutral ratings and 23 points per team; do not use prior-season or later-week data.
 * - Apply true home-field advantage, but skip the offseason rest differential.
 * - No additional early-season multiplier, weather, prime-time, rivalry or must-win adjustment.
 * - Keep confidenceValidated's existing actual-margin-band meaning; it is not probability calibration.
 *
 * **Compatibility and Evaluation:**
 * - Only passRushModel settings drive this calculation. Inherited legacy fields satisfy the shared
 *   configuration shape; they do not add legacy weights or bonuses to the new calculation.
 * - Preserve the v1.4.x calculation path; select v1.4.1 explicitly to continue its ongoing season.
 * - Atlanta/Baltimore reproduces 26.1555-22.5615 and 57.4321% for Atlanta with pre-Week-5 inputs.
 *   This verifies the worked arithmetic. The completed 895-game evaluation is documented in the design spec.
 * - No accuracy improvement is promised; compare matching graded games through the existing admin flow.
 *
 * @see docs/predictive-analysis/model-v1.5.0-design-spec.md for formulas, source semantics and run instructions.
 */
PREDICTION_MODEL_VERSIONS['1.5.0'] = {
  ...PREDICTION_MODEL_VERSIONS['1.4.1'],
  version: '1.5.0',
  description: 'Opponent-adjusted pass/rush efficiency with opportunity-based restraint.',
  passRushModel: {
    recentGames: 3, // Last three actual games; a bye does not consume a slot
    recentWeight: 0.3, // 30% recent rate + 70% season rate, each aggregated by opportunities
    opponentCorrection: 0.5, // Half of the restrained opponent-vs-league rate difference
    efficiencyPriorGames: 3, // Convert to league-average attempts/dropbacks for each metric
    eventPriorGames: 6, // TD/INT opportunity prior; six actual games for lost-fumble rate
    componentCap: 3, // +/- normalized units per component, before weighting
    passingWeights: { yards: 0.6, touchdowns: 0.2, interceptions: 0.1, sacks: 0.1 },
    rushingWeights: { yards: 0.8, touchdowns: 0.2 },
    ballSecurityWeight: 0.05, // 5% lost fumbles/game; remaining 95% goes to pass/rush matchups
    weekOnePoints: 23, // Per-team scoring prior before venue adjustment; no rest adjustment in Week 1
    pointsPerRating: 4, // Expected points per unit of matchup assessment
    homeFieldMargin: 2, // Home-minus-away margin points at a true home venue; neutral sites get zero
    restPointsPerDay: 0.15, // Home-minus-away margin points per day of rest difference
    restDayCap: 7, // Clamp rest difference to +/-7 days, limiting its margin effect to +/-1.05
    probabilityScale: 12, // Logistic denominator: 100 / (1 + exp(-signedMargin / scale))
    mediumProbability: 60, // Inclusive favored-team probability threshold, in percent
    highProbability: 75, // Inclusive favored-team probability threshold, in percent
  },
};

/**
 * Helper function to get a model configuration by version
 */
export const getModelConfig = (version: string): IPredictionModelConfig => {
  const config = PREDICTION_MODEL_VERSIONS[version];
  if (!config) {
    throw new Error(
      `Model version ${version} not found. Available versions: ${Object.keys(PREDICTION_MODEL_VERSIONS).join(', ')}`,
    );
  }
  return config;
};

/**
 * Helper function to get the current active model configuration
 */
export const getCurrentModelConfig = (): IPredictionModelConfig => {
  return getModelConfig(CURRENT_MODEL_VERSION);
};

/**
 * Helper function to list all available model versions
 */
export const getAvailableModelVersions = (): string[] => {
  return Object.keys(PREDICTION_MODEL_VERSIONS);
};
